import {Component} from "@angular/core";
import {ActivatedRoute, Router} from "@angular/router";
import {WorkflowService} from "../workflow.service";
import {WorkflowStep} from "../workflow-step";
import {ProjectService} from "../../project/project.service";
import {DialogService} from "../../bootstrap-modal/dialog.service";
import {WorkflowTargetInfoModel} from "../models/workflow-target-info.model";
import {WorkflowStepStatus} from "../models/workflow-step-status.enum";
import {addressModelFactory} from "../../address/address.model";
import {DeliveryOpsInformationModel} from "./delivery-ops-information.model";
import {DisableOrderAcceptanceDialogComponent} from "./disable-order-acceptance-dialog.component";
import {handleErrors} from "../../misc/handle-errors.decorator";
import {RejectProjectDialogComponent} from "./reject-project-dialog.component";

@Component({
  templateUrl: "./delivery-ops-information.template.pug",
  providers: [
    ProjectService,
  ],
})
export class DeliveryOpsInformationComponent extends WorkflowStep {
  error: Error;
  showMsId: boolean = false;
  sourceDocumentationLink: string;
  projectId: string;
  projectRejected: boolean = false;
  targetSystem: string;
  isMigrationActive: boolean = false;
  deliveryInfoForm: DeliveryOpsInformationModel = new DeliveryOpsInformationModel();
  paymentTermOptions = [
    {id: "2D2M", value: "2D2M - 2nd day 2nd month"}, // Variables can't start with number
    {id: "EM02", value: "EM02 - 60 days end of month"},
    {id: "EM20", value: "EM20 - Due net - 20th of next month"},
    {id: "EM30", value: "EM30 - 30 days end of month"},
    {id: "EM60", value: "EM60 - 60 days end of month"},
    {id: "GI45", value: "GI45 - HP Services Global Invoicing45"},
    {id: "GI60", value: "GI60 - HP Services Global Invoicing60"},
    {id: "GI90", value: "GI90 - HP Services Global Invoicing90"},
    {id: "GIPT", value: "GIPT - Within 30 days due net-Gl Inv"},
    {id: "N120", value: "N120 - Within 120 days due net"},
    {id: "NT00", value: "NT00 - Payable immediately due net"},
    {id: "NT05", value: "NT05 - Within 5 days Due net"},
    {id: "NT10", value: "NT10 - Within 10 days Due net"},
    {id: "NT15", value: "NT15 - Within 15 days due net"},
    {id: "NT30", value: "NT30 - Within 30 days due net"},
    {id: "NT35", value: "NT35 - Within 35 days due net"},
    {id: "NT37", value: "NT37 - Within 37 days due net"},
    {id: "NT40", value: "NT40 - Within 40 days due net"},
    {id: "NT42", value: "NT42 - Within 42 days due net"},
    {id: "NT45", value: "NT45 - Within 45 days due net"},
    {id: "NT50", value: "NT50 - Within 50 days due net"},
    {id: "NT60", value: "NT60 - Within 60 days due net"},
    {id: "NT62", value: "NT62 - Within 62 days due net"},
    {id: "NT65", value: "NT65 - Within 65 days Due net"},
    {id: "NT75", value: "NT75 - Within 75 days due net"},
    {id: "NT90", value: "NT90 - Within 90 days due net"},
    {id: "ZCPA", value: "ZCPA - Charged to American Express Cd"},
    {id: "ZCPB", value: "ZCPB - Charged to Mastercard"},
    {id: "ZCPC", value: "ZCPC - Charged to Visacard"},
    {id: "ZCPD", value: "ZCPD - Charged to Discover Card"},
    {id: "ZCPF", value: "ZCPF - PayPal payable immediately due net - ZPAL HP PayPal"},
    {id: "ZCSO", value: "ZCSO - Credit Card Generic Term"},
  ];

  loadProject(projectId: string): void {
    this.loading = true;
    this.targetSystem = this.workflowService.workflowConfig.targetSystem;

    this.projectService.getProjectById(projectId)
      .toPromise()
      .then(
        (project: any = {}) => {
          this.deliveryInfoForm.changeCountry(addressModelFactory(project.address).country);
          this.deliveryInfoForm.parseProjectJson(project);
          this.projectRejected = project.rejected;
          this.isMigrationActive = project.isMigrationActive;
          this.showMsId = (project.country && project.country.geoRegionId === "EMEA");
          this.sourceDocumentationLink = project.sourceDocumentationLink;
          this.projectId = project.id;
          this.loading = false;
        },
        (error) => {
          this.loading = false;
          this.error = error;
        },
      );
  }

  ready(workflowTargetInfo: WorkflowTargetInfoModel) {
    this.loadProject(workflowTargetInfo.projectId);
  }

  @handleErrors()
  async autoSave(): Promise<boolean> {
    if (!this.deliveryInfoForm.getRawValue().orderAcceptance) {
      const message = await this.dialogService.showDialog(
        DisableOrderAcceptanceDialogComponent,
        {message: ""},
        {backdropColor: "rgba(0, 0, 0, 0.5)"},
      );

      if (message) {
        this.deliveryInfoForm.controls["orderAcceptanceDisableReason"].setValue(message);
      } else {
        this.error = new Error("A reason is required in order to disable order acceptance");
        return false;
      }
    }
    const {project, billToAddress, billToContact} = this.deliveryInfoForm.exportDeliveryOpsInformation(this.projectId);
    await this.projectService.saveDeliveryOpsInfo(project, billToAddress, billToContact).toPromise();

    if (this.deliveryInfoForm.getRawValue().orderAcceptance) {
      this.workflowService.setStepStatus(WorkflowStepStatus.Completed);
      return true;
    }

    this.workflowService.setStepStatus(WorkflowStepStatus.Blocked);
    return false;
  }

  checkDirty(): boolean {
    return this.deliveryInfoForm.dirty;
  }

  constructor(
    public route: ActivatedRoute,
    public router: Router,
    public workflowService: WorkflowService,
    private projectService: ProjectService,
    private dialogService: DialogService,
  ) {
    super();
  }

  public rejectProject(): void {
    RejectProjectDialogComponent.showRejectProjectDialogModal(
      this.dialogService,
      {
        projectId: this.projectId,
        workflowId: this.workflowService.workflowConfig.id,
      },
    )
      .subscribe((result: any) => {
        if (result.success) {
          // navigate to the Projects screen (The CA is done with this project)
          this.router.navigate(["/projects"]);
        }
      });
  }

  public DPToTxt(param): void {
    const date = this.deliveryInfoForm.getRawValue()[`${param}Picker`];
    this.deliveryInfoForm.controls[param].setValue(date);
  }

  public txtToDP(param): void {
    let date = this.deliveryInfoForm.getRawValue()[param];
    if (date && date.trim() === "") {
      date = null; // Allow date to be cleared out, but pass any invalid dates through so that we yell at the user
    }
    this.deliveryInfoForm.controls[`${param}Picker`].setValue(date);
  }

  changeContractAccountCategory() {

  }
}
